import { Component, input, output, ChangeDetectionStrategy } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import {
    aiOutcomeDisplay,
    chatRequest,
    DiagramChatRequest,
    DiagramPersonRow,
    DiagramSelection,
    DiagramSpawnedGroup,
    DiagramStage,
    executionOutcomeLabel,
    StageTypeStyles,
} from "./workflow.diagram.model";
import { WorkflowDiagramPersonComponent } from "./workflow.diagram.person.component";
import { WorkflowDiagramSpawnedRowsComponent } from "./workflow.diagram.spawned.rows.component";

export interface DiagramSelectEvent {
    selection: DiagramSelection;
    trigger: HTMLElement;
}

@TypeManagerDecorator("workflow-diagram-stages")
@Component({
    selector: "[workflow-diagram-stages]",
    template: `
        @for (stage of stages(); track stage.id) {
            <svg:g class="stage-group" [attr.data-status]="stage.status" [attr.data-color-type]="stage.colorType">
                <!-- Current stage glow outline -->
                @if (stage.status === "current") {
                    <rect
                        [attr.x]="stage.x - 12"
                        [attr.y]="stage.y - 12"
                        [attr.width]="stage.width + 24"
                        [attr.height]="stage.height + 24"
                        [attr.rx]="18"
                        [attr.fill]="getStageGradient(stage)"
                        opacity="0.35"
                    />
                    <rect
                        [attr.x]="stage.x - 4"
                        [attr.y]="stage.y - 4"
                        [attr.width]="stage.width + 8"
                        [attr.height]="stage.height + 8"
                        [attr.rx]="14"
                        fill="white"
                    />
                }

                @if (stage.status === "pending") {
                    <rect
                        [attr.x]="stage.x - 4"
                        [attr.y]="stage.y - 4"
                        [attr.width]="stage.width + 8"
                        [attr.height]="stage.height + 8"
                        [attr.rx]="14"
                        [attr.fill]="getStageGradient(stage)"
                        opacity="0.5"
                    />
                }

                <rect
                    [attr.x]="stage.x"
                    [attr.y]="stage.y"
                    [attr.width]="stage.width"
                    [attr.height]="stage.height"
                    [attr.rx]="12"
                    [attr.fill]="getStageGradient(stage)"
                    class="stage-rect"
                    [attr.filter]="'url(#shadow-' + svgId() + ')'"
                ></rect>

                @if (stage.status === "pending") {
                    <rect
                        [attr.x]="stage.x"
                        [attr.y]="stage.y"
                        [attr.width]="stage.width"
                        [attr.height]="stage.height"
                        [attr.rx]="12"
                        fill="none"
                        stroke="rgba(255,255,255,0.8)"
                        stroke-width="2"
                        stroke-dasharray="8 4"
                    />
                }

                <rect
                    [attr.x]="stage.x"
                    [attr.y]="stage.y"
                    [attr.width]="stage.width"
                    [attr.height]="stage.headerHeight"
                    [attr.rx]="12"
                    fill="rgba(255, 255, 255, 0.15)"
                ></rect>

                <foreignObject
                    [attr.x]="stage.x"
                    [attr.y]="stage.y"
                    [attr.width]="stage.width"
                    [attr.height]="stage.height"
                >
                    <div xmlns="http://www.w3.org/1999/xhtml" class="stage-body">
                        <button
                            type="button"
                            class="stage-header"
                            [style.height.px]="stage.headerHeight"
                            [attr.aria-controls]="paneId()"
                            (click)="emitSelect({ kind: 'stage', stageId: stage.id }, $event)"
                        >
                            <span class="stage-icon" aria-hidden="true">{{ glyph(stage) }}</span>
                            <span class="stage-title">
                                <span class="stage-label">{{ stage.name }}</span>
                                @if (stage.flagLabels.length) {
                                    <span class="stage-flags">{{ stage.flagLabels.join(" · ") }}</span>
                                }
                            </span>
                            <span class="visuallyhidden">
                                , {{ typeLabel(stage) }} stage{{ stage.statusLabel ? ", " + stage.statusLabel : "" }}
                            </span>
                        </button>

                        @if (!compactMode()) {
                            <div class="stage-sections">
                                @if (stage.actions.length) {
                                    <div class="section">
                                        <div class="section-label">Actions</div>
                                        @for (action of stage.actions; track action.id) {
                                            <div class="action-row">
                                                <button
                                                    type="button"
                                                    class="row-button action-button"
                                                    [attr.aria-controls]="paneId()"
                                                    (click)="
                                                        emitSelect(
                                                            { kind: 'action', stageId: stage.id, actionId: action.id },
                                                            $event
                                                        )
                                                    "
                                                >
                                                    <span class="action-icon" aria-hidden="true">⚡</span>
                                                    @if (action.spawnFormNames?.length) {
                                                        @let formNames = action.spawnFormNames!.join(", ");
                                                        <span class="action-text">
                                                            <span class="action-label-text action-label-text--single">
                                                                {{ action.label }}
                                                            </span>
                                                            <span class="visuallyhidden">, </span>
                                                            <span class="action-form" [attr.title]="formNames">
                                                                {{ formNames }}
                                                            </span>
                                                        </span>
                                                    } @else {
                                                        <span class="action-label-text">{{ action.label }}</span>
                                                    }
                                                    @if (showProgress()) {
                                                        <span class="visuallyhidden">
                                                            , {{ executionOutcomeLabel(action.detail?.execution) }}
                                                        </span>
                                                    }
                                                </button>
                                            </div>
                                            @if (action.spawned; as spawned) {
                                                <workflow-diagram-spawned-rows
                                                    [group]="spawned"
                                                    [paneId]="paneId()"
                                                    [chatEnabled]="chatEnabled()"
                                                    (open)="selectSpawned(stage, spawned, $event)"
                                                    (more)="selectSpawned(stage, spawned, $event, true)"
                                                    (chatWith)="chatWith.emit($event)"
                                                />
                                            }
                                        }
                                    </div>
                                }

                                @if (stage.accumulatorSpawned; as accumulated) {
                                    <div class="section">
                                        <div class="section-label">Spawned forms</div>
                                        <div class="spawned-rollup">{{ accumulated.rollupLabel }}</div>
                                        <workflow-diagram-spawned-rows
                                            [group]="accumulated"
                                            [paneId]="paneId()"
                                            [chatEnabled]="chatEnabled()"
                                            (open)="selectSpawned(stage, accumulated, $event)"
                                            (more)="selectSpawned(stage, accumulated, $event, true)"
                                            (chatWith)="chatWith.emit($event)"
                                        />
                                    </div>
                                }

                                @if (stage.submitter; as submitter) {
                                    <div class="section">
                                        <div class="section-label">Submitted by</div>
                                        <div class="approval-row">
                                            <workflow-diagram-person
                                                [person]="submitter"
                                                [chatEnabled]="chatEnabled()"
                                                [onDark]="true"
                                                (chat)="emitChat(submitter, $event)"
                                            />
                                        </div>
                                    </div>
                                }

                                @if (stage.approvalRows.length) {
                                    <div class="section">
                                        <div class="section-label">Approvals</div>
                                        @for (row of stage.approvalRows; track row.key) {
                                            <div class="approval-row">
                                                @switch (row.kind) {
                                                    @case ("person") {
                                                        <workflow-diagram-person
                                                            [person]="row"
                                                            [chatEnabled]="chatEnabled()"
                                                            [showStatus]="showProgress()"
                                                            [onDark]="true"
                                                            (chat)="emitChat(row, $event)"
                                                        />
                                                    }
                                                    @case ("ai") {
                                                        @let ai = aiOutcomeDisplay(row.outcome);
                                                        <span class="row-avatar" aria-hidden="true">
                                                            <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" />
                                                        </span>
                                                        <div class="row-info">
                                                            <div class="row-name">
                                                                AI Approver{{
                                                                    row.stateName ? " — " + row.stateName : ""
                                                                }}
                                                            </div>
                                                            <div class="row-status" [attr.data-tone]="ai.tone">
                                                                <s25-ng-icon [type]="ai.icon" [ariaLabel]="false" />
                                                                <span>{{ ai.label }}</span>
                                                            </div>
                                                        </div>
                                                    }
                                                    @case ("configured") {
                                                        <span class="row-avatar" aria-hidden="true">
                                                            <s25-ng-icon
                                                                [type]="row.detail === 'Contact' ? 'user' : 'users'"
                                                                [ariaLabel]="false"
                                                            />
                                                        </span>
                                                        <div class="row-info">
                                                            <div class="row-name">{{ row.label }}</div>
                                                            <div class="row-status">{{ row.detail }}</div>
                                                        </div>
                                                    }
                                                }
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        } @else if (stage.states.length || stage.actions.length) {
                            <div class="compact-indicators" aria-hidden="true">
                                @if (stage.states.length) {
                                    <span class="compact-count">{{ stage.states.length }}</span>
                                }
                                @if (stage.actions.length) {
                                    <span class="compact-actions">⚡</span>
                                }
                            </div>
                        }
                    </div>
                </foreignObject>
            </svg:g>
        }
    `,
    styles: `
        .stage-rect {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .stage-body {
            height: 100%;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            color: #fff;
            overflow: hidden;
        }

        button {
            font: inherit;
            color: inherit;
        }

        .stage-header {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 0 10px;
            border: none;
            border-radius: 12px 12px 0 0;
            background: transparent;
            text-align: left;
            cursor: pointer;
        }

        .stage-header:hover {
            background: rgba(255, 255, 255, 0.1);
        }

        .stage-header:focus-visible,
        .row-button:focus-visible {
            outline: 2px solid #fff;
            outline-offset: -3px;
        }

        .stage-icon {
            flex-shrink: 0;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.25);
            font-size: 12px;
        }

        .stage-title {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
        }

        .stage-label {
            font-weight: 600;
            font-size: 16px;
            line-height: 1.25;
            overflow-wrap: break-word;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .stage-flags {
            height: 18px;
            line-height: 18px;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.3px;
            text-transform: uppercase;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .stage-sections {
            padding: 5px 12px 0;
        }

        .section + .section {
            margin-top: 12px;
        }

        .section-label {
            height: 22px;
            line-height: 22px;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            color: rgba(255, 255, 255, 0.92);
        }

        .action-row {
            height: 40px;
            padding-bottom: 4px;
            box-sizing: border-box;
        }

        .row-button {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 0 8px;
            border: none;
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.15);
            text-align: left;
            font-size: 14px;
            line-height: 1.15;
            cursor: pointer;
        }

        .row-button:hover {
            background: rgba(255, 255, 255, 0.3);
        }

        .action-icon {
            font-size: 20px;
            color: #ffd700;
        }

        .action-label-text {
            flex: 1;
            min-width: 0;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .action-text {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
        }

        .action-label-text--single {
            flex: none;
            -webkit-line-clamp: 1;
        }

        .action-form {
            font-size: 12px;
            color: rgba(255, 255, 255, 0.92);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .spawned-rollup {
            height: 20px;
            line-height: 20px;
            font-size: 12px;
            font-weight: 600;
        }

        .approval-row {
            height: 48px;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 2px 0 6px;
            box-sizing: border-box;
        }

        .approval-row workflow-diagram-person {
            flex: 1;
            min-width: 0;
        }

        .row-avatar {
            flex-shrink: 0;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.25);
            --icon-size: 20px;
        }

        .row-info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 1px;
        }

        .row-name {
            font-size: 13px;
            font-weight: 500;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .row-status {
            display: flex;
            align-items: center;
            gap: 3px;
            font-size: 11px;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.92);
            white-space: nowrap;
            --icon-size: 14px;
        }

        .row-status[data-tone="approved"] s25-ng-icon {
            color: #4ade80;
        }
        .row-status[data-tone="denied"] s25-ng-icon {
            color: #fecaca;
        }
        .row-status[data-tone="changes"] s25-ng-icon {
            color: #fde68a;
        }
        .row-status[data-tone="deferred"] s25-ng-icon {
            color: #a5f3fc;
        }

        .compact-indicators {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6px 12px;
        }

        .compact-count {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.25);
            font-size: 14px;
            font-weight: 700;
        }

        .compact-actions {
            font-size: 22px;
            color: #ffd700;
        }

        .stage-group:hover .stage-rect {
            transform: translateY(-2px);
        }

        @media (prefers-reduced-motion: reduce) {
            .stage-rect {
                transition: none;
            }
            .stage-group:hover .stage-rect {
                transform: none;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent, WorkflowDiagramPersonComponent, WorkflowDiagramSpawnedRowsComponent],
})
export class WorkflowDiagramStagesComponent {
    stages = input<DiagramStage[]>();
    compactMode = input<boolean>();
    showProgress = input(false);
    chatEnabled = input(false);
    paneId = input<string>();
    svgId = input.required<string>();

    selectItem = output<DiagramSelectEvent>();
    chatWith = output<DiagramChatRequest>();
    /** "+N more" activations, for telemetry. */
    spawnedMore = output<void>();

    protected readonly aiOutcomeDisplay = aiOutcomeDisplay;
    protected readonly executionOutcomeLabel = executionOutcomeLabel;

    getStageGradient(stage: DiagramStage): string {
        return `url(#grad-${stage.colorType}-${this.svgId()})`;
    }

    glyph(stage: DiagramStage): string {
        return StageTypeStyles[stage.colorType].glyph;
    }

    typeLabel(stage: DiagramStage): string {
        return StageTypeStyles[stage.type].label;
    }

    protected emitSelect(selection: DiagramSelection, event: Event) {
        this.selectItem.emit({ selection, trigger: event.currentTarget as HTMLElement });
    }

    protected selectSpawned(stage: DiagramStage, group: DiagramSpawnedGroup, trigger: HTMLElement, more = false) {
        this.selectItem.emit({ selection: { kind: "spawned", stageId: stage.id, groupKey: group.key }, trigger });
        if (more) this.spawnedMore.emit();
    }

    protected emitChat(person: DiagramPersonRow, trigger: HTMLElement) {
        const request = chatRequest(person, trigger);
        if (request) this.chatWith.emit(request);
    }
}
